import { Button } from '~/components/button';
import { Utility } from '~/components/dropdowns';
import { Form } from '~/components/form';
import { type ImageType, SELECT_UTILITY_IMAGE, ImageSelector } from '~/components/image';
import { Inputs } from '~/components/inputs';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Description, Title } from '~/components/typography';
import { useCopy, useForm } from '~/hooks';

export const SelectUtility = ({ context, next, overrideCopy }: SelectUtilityProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const [form, updateForm, isValid] = useForm({
    utilityId: context?.utilityId || '',
    zipCode: context?.zipCode || '',
  });

  return (
    <TwoColumnGrid testId="screen-SelectUtility">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={0}>{copy.description}</Description>
        {copy.descriptionTwo && (
          <Description mb={0} mt={5}>
            {copy.descriptionTwo}
          </Description>
        )}
        <Form
          isValid={isValid}
          onSubmit={() => {
            next(form as SelectUtilityForm);
          }}
        >
          <Container addInputMargin marginY={7}>
            <Inputs.ZipCode
              label={copy.zipCodeLabel}
              placeholder={copy.zipCodePlaceholder}
              onChange={updateForm}
              value={form.zipCode}
            />
            <Utility onChange={updateForm} value={Number(form.utilityId)} zipCode={form.zipCode} />
          </Container>
          <Button disabled={!isValid} type="submit" mode="primary">
            {copy.next}
          </Button>
        </Form>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = () => ({
  title: 'Check if community solar is available in your zip code.',
  description: 'Enter your zip code and your utility to check availability.',
  descriptionTwo: '',
  imageType: SELECT_UTILITY_IMAGE as ImageType,
  next: 'Continue',
  zipCodeLabel: 'Zip code',
  zipCodePlaceholder: 'Enter your zip code',
});

export type SelectUtilityCopy = ReturnType<typeof defaultCopy>;

export interface SelectUtilityProps {
  context: SelectUtilityContext;
  next: (payload: SelectUtilityForm) => void;
  overrideCopy?: (context: SelectUtilityContext) => Partial<SelectUtilityCopy>;
}

export interface SelectUtilityForm {
  utilityId: number;
  zipCode: string;
}

export interface SelectUtilityContext {
  utilityId?: number;
  zipCode?: string;
}
